import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { ScrollText, Search, Filter } from 'lucide-react';

const actionColors = {
  'tenant.created': 'text-[#10B981] bg-[#10B981]/10 border-[#10B981]/20',
  'tenant.updated': 'text-[#3B82F6] bg-[#3B82F6]/10 border-[#3B82F6]/20',
  'tenant.archived': 'text-[#F43F5E] bg-[#F43F5E]/10 border-[#F43F5E]/20',
  'user.invited': 'text-[#EF694C] bg-[#EF694C]/10 border-[#EF694C]/20',
  'user.updated': 'text-[#F59E0B] bg-[#F59E0B]/10 border-[#F59E0B]/20',
  'channel.configured': 'text-[#a1a1aa] bg-white/5 border-white/10',
  'knowledge.added': 'text-[#EF694C] bg-[#EF694C]/10 border-[#EF694C]/20',
};

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const limit = 20;

  useEffect(() => {
    api.get(`/superadmin/audit?limit=${limit}&skip=${page * limit}`)
      .then(r => { setLogs(r.data.logs); setTotal(r.data.total); })
      .catch(() => toast.error('Failed to load audit logs'))
      .finally(() => setLoading(false));
  }, [page]);

  const filtered = logs.filter(l =>
    l.action.includes(search.toLowerCase()) ||
    l.user_name?.toLowerCase().includes(search.toLowerCase()) ||
    l.resource_type?.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h2 className="font-heading font-bold text-2xl text-white">Audit Log</h2>
          <p className="text-[#a1a1aa] text-sm mt-1">{total} total entries</p>
        </div>
      </div>

      <div className="relative animate-fade-in-up stagger-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525b]" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Nach Aktion, Benutzer oder Ressource filtern..."
          data-testid="audit-search-input"
          className="w-full max-w-md h-10 bg-[#121214] border border-white/10 rounded-lg pl-9 pr-4 text-white text-sm placeholder-[#52525b] focus:outline-none focus:border-[#EF694C] transition-all" />
      </div>

      <div className="bg-[#121214] border border-white/10 rounded-xl overflow-hidden animate-fade-in-up stagger-2">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#EF694C]/30 border-t-[#EF694C] rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <ScrollText className="w-12 h-12 text-[#52525b] mx-auto mb-3" />
            <p className="text-[#a1a1aa]">No audit logs found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                {['Timestamp', 'User', 'Action', 'Resource', 'Details'].map(h => (
                  <th key={h} className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#52525b]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map(log => (
                <tr key={log.id} className="hover:bg-white/[0.03] transition-colors" data-testid={`audit-row-${log.id}`}>
                  <td className="px-6 py-3.5">
                    <span className="text-xs font-mono text-[#52525b]">{formatDate(log.created_at)}</span>
                  </td>
                  <td className="px-6 py-3.5">
                    <span className="text-sm text-[#a1a1aa]">{log.user_name || '—'}</span>
                  </td>
                  <td className="px-6 py-3.5">
                    <span className={`text-xs px-2.5 py-1 rounded-md border font-medium ${actionColors[log.action] || 'text-[#a1a1aa] bg-white/5 border-white/10'}`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="px-6 py-3.5">
                    <span className="text-xs text-[#a1a1aa] capitalize">{log.resource_type}</span>
                  </td>
                  <td className="px-6 py-3.5">
                    <span className="text-xs text-[#52525b] font-mono">
                      {log.metadata ? JSON.stringify(log.metadata).slice(0, 60) : '—'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > limit && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-[#52525b]">Page {page + 1} of {Math.ceil(total / limit)}</p>
          <div className="flex gap-2">
            <button disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="audit-prev-btn"
              className="px-3 h-8 text-sm text-[#a1a1aa] border border-white/10 rounded-lg hover:bg-white/5 disabled:opacity-40 transition-all">Prev</button>
            <button disabled={(page + 1) * limit >= total} onClick={() => setPage(p => p + 1)} data-testid="audit-next-btn"
              className="px-3 h-8 text-sm text-[#a1a1aa] border border-white/10 rounded-lg hover:bg-white/5 disabled:opacity-40 transition-all">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
